import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

const EMAIL_GATE_KEY = "dna_email_captured";

export default function Results() {
  const [result, setResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [email, setEmail] = useState("");
  const [emailUnlocked, setEmailUnlocked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("dna_result");
    if (raw) setResult(JSON.parse(raw));
    const rawIds = sessionStorage.getItem("dna_selected");
    if (rawIds) setSelectedIds(JSON.parse(rawIds));
    // If the user already unlocked this result in this session, keep it unlocked
    // on back-nav / refresh. Tied to the specific result id so a new quiz re-gates.
    const unlockedFor = sessionStorage.getItem(EMAIL_GATE_KEY);
    if (raw && unlockedFor) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed.id && unlockedFor === parsed.id) setEmailUnlocked(true);
      } catch (err) {
        console.error("Failed to parse stored DNA result:", err);
      }
    }
  }, []);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!result || saving) return;
    setSaving(true);
    try {
      await api.captureEmail({
        email,
        primary_slug: result.primary.slug,
        secondary_slug: result.secondary.slug,
        image_ids: selectedIds,
      });
      sessionStorage.setItem(EMAIL_GATE_KEY, result.id);
      setEmailUnlocked(true);
      toast.success("Unlocking your Design DNA…");
    } catch (err) {
      toast.error("Couldn't save your email. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="results-empty">
        <div className="text-center max-w-md">
          <p className="overline mb-4">Nothing to reveal yet</p>
          <h2 className="font-serif-display text-3xl md:text-4xl mb-6">Take the quiz first.</h2>
          <Link to="/quiz" className="btn-primary" data-testid="results-restart-empty">Start the quiz →</Link>
        </div>
      </div>
    );
  }

  // ── EMAIL GATE ─────────────────────────────────────────────────────────
  // No result content — no style name, no description, no traits, no palette —
  // is rendered until the user submits their email.
  if (!emailUnlocked) {
    return (
      <div className="min-h-screen grain flex flex-col" data-testid="results-email-gate">
        {/* Nav */}
        <div className="container-editorial pt-8 pb-6 flex justify-between items-center">
          <Link to="/" className="font-serif-display text-2xl md:text-3xl">
            Aesthos<span className="text-[color:var(--terracotta)]">.</span>
          </Link>
          <div className="overline hidden sm:block">Your Design DNA</div>
        </div>
        <div className="hairline container-editorial" />

        <div className="flex-1 flex items-center">
          <div className="container-editorial w-full py-16 md:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-7 fade-up">
                <p className="overline mb-6" data-testid="gate-eyebrow">
                  Your reading is ready
                </p>
                <h1
                  className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tight text-[color:var(--ink)]"
                  data-testid="gate-headline"
                >
                  Enter your email
                  <br />
                  to reveal your
                  <br />
                  <em className="italic text-[color:var(--terracotta)]">Design DNA.</em>
                </h1>
                <p className="text-base md:text-lg text-[color:var(--ink-soft)] mt-8 max-w-md leading-relaxed">
                  Primary style, secondary style, signature traits, materials and palette
                  — sent to your inbox and revealed on the next screen.
                </p>
              </div>

              <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
                <form onSubmit={submitEmail} className="space-y-6" data-testid="email-gate-form">
                  <div>
                    <label htmlFor="email-gate-input" className="overline block mb-3">
                      Email address
                    </label>
                    <input
                      id="email-gate-input"
                      type="email"
                      required
                      autoFocus
                      placeholder="you@somewhere.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-transparent border-b border-[color:var(--ink)] py-4 px-1 text-lg placeholder:text-[color:var(--ink-soft)] focus:outline-none"
                      data-testid="email-gate-input"
                    />
                  </div>
                  <button
                    className="btn-primary w-full sm:w-auto"
                    type="submit"
                    disabled={saving}
                    data-testid="email-gate-submit"
                  >
                    {saving ? "Unlocking…" : "Reveal my Design DNA"}
                    <span aria-hidden>→</span>
                  </button>
                  <p className="overline" style={{ fontSize: "0.6rem" }}>
                    One email · No spam · Unsubscribe any time
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── UNLOCKED RESULT ────────────────────────────────────────────────────
  const { primary, secondary, ranking } = result;

  return (
    <div className="min-h-screen pb-32 grain" data-testid="results-page">
      {/* Nav */}
      <div className="container-editorial pt-8 pb-6 flex justify-between items-center">
        <Link to="/" className="font-serif-display text-2xl md:text-3xl">
          Aesthos<span className="text-[color:var(--terracotta)]">.</span>
        </Link>
        <div className="overline hidden sm:block">Your Design DNA</div>
      </div>
      <div className="hairline container-editorial" />

      {/* Hero */}
      <section className="container-editorial pt-16 md:pt-24 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8 fade-up">
            <p className="overline mb-6">Your primary style</p>
            <h1
              className="font-serif-display text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] leading-[0.9] tracking-tight text-[color:var(--ink)] italic"
              data-testid="result-primary-name"
            >
              {primary.name}.
            </h1>
            <p className="overline mt-8">
              With a secondary pull toward{" "}
              <span
                className="not-italic text-[color:var(--ink)] font-serif-display text-lg md:text-xl italic"
                data-testid="result-secondary-name"
              >
                {secondary.name}
              </span>
            </p>
          </div>
          <div className="lg:col-span-4 fade-up" style={{ animationDelay: "0.15s" }}>
            <p className="text-base md:text-lg leading-relaxed text-[color:var(--ink-soft)]" data-testid="result-description">
              {primary.description}
            </p>
          </div>
        </div>
      </section>

      {/* Signature traits */}
      <section className="container-editorial pb-20 md:pb-24">
        <div className="hairline mb-10" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <p className="overline">Signature traits</p>
          </div>
          <ul className="lg:col-span-9 space-y-4 md:space-y-6" data-testid="result-signature-traits">
            {primary.signature_traits.map((t, i) => (
              <li key={`${primary.slug}-trait-${t}`} className="flex gap-6 items-start">
                <span className="font-serif-display text-2xl md:text-3xl text-[color:var(--terracotta)] w-8 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif-display text-xl md:text-2xl lg:text-3xl leading-snug text-[color:var(--ink)]">
                  {t.charAt(0).toUpperCase() + t.slice(1)}.
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Materials */}
      <section className="container-editorial pb-20 md:pb-24">
        <div className="hairline mb-10" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <p className="overline">Materials</p>
          </div>
          <div className="lg:col-span-9 flex flex-wrap gap-x-8 gap-y-3" data-testid="result-materials">
            {primary.materials.map((m, i) => (
              <span key={`${primary.slug}-mat-${m}`} className="font-serif-display text-2xl md:text-3xl italic text-[color:var(--ink)]">
                {m}
                {i < primary.materials.length - 1 && <span className="text-[color:var(--ink-soft)] not-italic ml-8">·</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Color palette */}
      <section className="container-editorial pb-20 md:pb-24">
        <div className="hairline mb-10" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <p className="overline">Color palette</p>
          </div>
          <div className="lg:col-span-9 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6" data-testid="result-color-palette">
            {primary.color_palette.map((c, i) => (
              <div key={`${primary.slug}-swatch-${c.hex}`} className="flex flex-col gap-3">
                <div
                  className="w-full aspect-[4/5] border border-[color:var(--border)]"
                  style={{ background: c.hex }}
                  aria-label={c.name}
                />
                <div>
                  <p className="text-sm md:text-base font-medium text-[color:var(--ink)]">{c.name}</p>
                  <p className="overline mt-1" style={{ fontSize: "0.6rem" }}>{c.hex}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Secondary quick view */}
      <section className="container-editorial pb-20 md:pb-24">
        <div className="hairline mb-10" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <p className="overline">Your secondary pull</p>
          </div>
          <div className="lg:col-span-9">
            <h3 className="font-serif-display text-3xl md:text-5xl italic text-[color:var(--ink)] mb-4" data-testid="secondary-heading">
              {secondary.name}
            </h3>
            <p className="text-base md:text-lg text-[color:var(--ink-soft)] leading-relaxed max-w-3xl">
              {secondary.description}
            </p>
          </div>
        </div>
      </section>

      {/* Consultation CTA — turn inspiration into a real home */}
      <section className="container-editorial pb-20 md:pb-24" data-testid="consultation-cta">
        <div className="hairline mb-10" />
        <div className="bg-[color:var(--ink)] text-[color:var(--bg)] px-6 py-16 md:px-16 md:py-24 relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 relative z-10">
            <div className="lg:col-span-8">
              <p
                className="overline mb-6"
                style={{ color: "var(--terracotta)" }}
                data-testid="consult-eyebrow"
              >
                Book a consultation
              </p>
              <h2
                className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8"
                data-testid="consult-headline"
              >
                Now that you&rsquo;ve discovered your design style,
                <br />
                <em className="italic" style={{ color: "var(--terracotta)" }}>
                  why stop at inspiration?
                </em>
              </h2>
              <p className="font-serif-display text-lg md:text-xl leading-relaxed opacity-90 max-w-2xl">
                If you&rsquo;d like to turn your style into a beautiful, well-designed home, book
                a personalised Interior Design Guidance Session. I&rsquo;ll help you translate
                your style into practical design decisions&mdash;from layouts and colours to
                furniture, materials, lighting, and d&eacute;cor&mdash;so you can confidently
                create your space.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col justify-end gap-4">
              <a
                href="https://consult.aesthos.in/interiorconsultations"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-5 bg-[color:var(--bg)] text-[color:var(--ink)] hover:bg-[color:var(--terracotta)] hover:text-[color:var(--bg)] transition-colors duration-300 text-xs md:text-sm tracking-[0.2em] uppercase font-medium"
                data-testid="consult-cta-button"
              >
                Book your consultation
                <span aria-hidden>→</span>
              </a>
              <p
                className="overline opacity-60 break-words"
                style={{ fontSize: "0.6rem", color: "var(--bg)" }}
              >
                consult.aesthos.in/interiorconsultations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Email capture — user already unlocked; show confirmation */}
      <section className="container-editorial pb-20 md:pb-24">
        <div className="hairline mb-10" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-5">
            <p className="overline mb-4">Saved to your inbox</p>
            <h3 className="font-serif-display text-3xl md:text-5xl leading-tight tracking-tight text-[color:var(--ink)]">
              Sent your way.
            </h3>
            <p className="text-sm md:text-base text-[color:var(--ink-soft)] mt-4 max-w-md">
              Your Design DNA — palette, materials and all — is on its way to your inbox.
            </p>
          </div>
          <div className="lg:col-span-7">
            <p
              className="font-serif-display text-2xl md:text-3xl italic text-[color:var(--terracotta)]"
              data-testid="email-capture-success"
            >
              Saved. Watch your inbox.
            </p>
          </div>
        </div>
      </section>

      {/* Ranking / debug transparency */}
      {ranking && ranking.length > 0 && (
        <section className="container-editorial pb-20 md:pb-24">
          <div className="hairline mb-10" />
          <details className="group" data-testid="score-breakdown">
            <summary className="overline cursor-pointer list-none flex items-center gap-2">
              How we read your picks
              <span className="text-[color:var(--terracotta)] group-open:rotate-45 transition-transform">＋</span>
            </summary>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 max-w-2xl">
              {ranking.slice(0, 8).map((r) => (
                <div key={r.slug} className="flex justify-between text-sm border-b border-[color:var(--border)] py-2">
                  <span className="text-[color:var(--ink)]">
                    {r.slug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                  <span className="text-[color:var(--ink-soft)]">{r.score}</span>
                </div>
              ))}
            </div>
          </details>
        </section>
      )}

      {/* Restart */}
      <section className="container-editorial pb-16">
        <div className="hairline mb-10" />
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="font-serif-display text-2xl md:text-3xl italic">Feel like your pull has shifted?</p>
          <Link to="/quiz" className="btn-secondary" data-testid="results-restart">
            Retake the quiz →
          </Link>
        </div>
      </section>
    </div>
  );
}
